import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Loader2 } from 'lucide-react';
import { RoleLoginShell } from '@/components/auth/RoleLoginShell';
import { ROLE_LOGIN_THEMES } from '@/components/auth/roleLoginThemes';
import { forgotPassword, requestOtp, resetPasswordSms } from '@/api/auth';

const THEME = ROLE_LOGIN_THEMES["login"];

/**
 * Self-service password reset request.
 *
 * Two paths, chosen by tab:
 * - Email: the backend always answers with the same neutral 200 — even for
 *   unknown addresses — so this page always lands on the "sent" notice.
 * - Mobile · OTP: sends a `reset_password`-purpose OTP to the linked phone
 *   (same neutral behaviour for unknown numbers), then verifies the code and
 *   sets the new password in one step.
 */
export function ForgotPasswordPage() {
  const [mode, setMode] = useState<'email' | 'sms'>('email');
  // Email path
  const [email, setEmail] = useState("");
  const [sent, setSent] = useState(false);
  // SMS path
  const [phoneNumber, setPhoneNumber] = useState("");
  const [countryCode, setCountryCode] = useState("91");
  const [smsStep, setSmsStep] = useState<'phone' | 'otp' | 'done'>('phone');
  const [otpCode, setOtpCode] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [devOtp, setDevOtp] = useState("");
  const [resendTimer, setResendTimer] = useState(0);
  // Shared
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (resendTimer <= 0) return;
    const interval = setInterval(() => setResendTimer(t => t - 1), 1000);
    return () => clearInterval(interval);
  }, [resendTimer]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email.trim()) return;
    setLoading(true);
    setError("");
    try {
      await forgotPassword(email.trim());
      setSent(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const sendResetOtp = async () => {
    const data = await requestOtp(phoneNumber, countryCode, 'reset_password');
    if (data.otp) setDevOtp(data.otp);
    setResendTimer(60);
  };

  const handleRequestOtp = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!phoneNumber.trim() || phoneNumber.length < 10) {
      setError("Please enter a valid mobile number");
      return;
    }
    setError("");
    setLoading(true);
    try {
      await sendResetOtp();
      setSmsStep('otp');
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to send OTP");
    } finally {
      setLoading(false);
    }
  };

  const handleResendOtp = async () => {
    if (resendTimer > 0) return;
    setError("");
    setLoading(true);
    try {
      await sendResetOtp();
      setOtpCode("");
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to resend OTP");
    } finally {
      setLoading(false);
    }
  };

  const handleSmsReset = async (e: React.FormEvent) => {
    e.preventDefault();
    if (otpCode.length < 4) {
      setError("Please enter the complete OTP");
      return;
    }
    if (newPassword.length < 8) {
      setError("Password must be at least 8 characters");
      return;
    }
    if (newPassword !== confirmPassword) {
      setError("Passwords do not match");
      return;
    }
    setError("");
    setLoading(true);
    try {
      await resetPasswordSms(phoneNumber, countryCode, otpCode, newPassword);
      setSmsStep('done');
    } catch (err) {
      setError(err instanceof Error ? err.message : "Password reset failed");
    } finally {
      setLoading(false);
    }
  };

  const switchMode = (next: 'email' | 'sms') => {
    setMode(next);
    setError("");
  };

  const meta = (
    <p className="rl__meta-row">
      Remembered your password? <Link to="/login">Back to sign in</Link>
    </p>
  );

  return (
    <RoleLoginShell theme={THEME} meta={meta}>
      {sent && mode === 'email' ? (
        <>
          <div className="rl__notice" role="status">
            If an account exists for <strong>{email.trim()}</strong>, we&apos;ve sent a
            password reset link. It expires in 1 hour — check your spam folder if it
            doesn&apos;t arrive.
          </div>
          <p className="rl__meta-row">
            Didn&apos;t get it? You can request another link in a few minutes.
          </p>
          <Link className="rl__submit-link" to="/login">
            Back to sign in
          </Link>
        </>
      ) : mode === 'sms' && smsStep === 'done' ? (
        <>
          <div className="rl__notice" role="status">
            Your password has been reset. Sign in with your new password.
          </div>
          <Link className="rl__submit-link" to="/login">
            Back to sign in
          </Link>
        </>
      ) : (
        <>
          <div className="rl__tabs" role="tablist" aria-label="Reset method">
            <button
              type="button"
              role="tab"
              className="rl__tab"
              aria-pressed={mode === 'email'}
              onClick={() => switchMode('email')}
            >
              Email link
            </button>
            <button
              type="button"
              role="tab"
              className="rl__tab"
              aria-pressed={mode === 'sms'}
              onClick={() => switchMode('sms')}
            >
              Mobile · OTP
            </button>
          </div>

          {error && <div className="rl__error" role="alert">{error}</div>}

          {mode === 'email' && (
            <>
              <p className="rl__otp-note">
                Enter your account email and we&apos;ll send you a link to reset your password.
              </p>
              <form className="rl__form" onSubmit={handleSubmit}>
                <div className="rl__field">
                  <label className="rl__label" htmlFor="forgot-email">Email</label>
                  <input id="forgot-email" className="rl__input" type="email" value={email}
                    onChange={e => setEmail(e.target.value)} placeholder="you@example.com"
                    required autoFocus autoComplete="email" />
                </div>
                <button type="submit" className="rl__submit" disabled={loading || !email.trim()}>
                  {loading ? <><Loader2 size={16} className="rl__spin" /> Sending...</> : "Send Reset Link"}
                </button>
              </form>
            </>
          )}

          {mode === 'sms' && smsStep === 'phone' && (
            <>
              <p className="rl__otp-note">
                Enter the mobile number linked to your account and we&apos;ll text you a
                one-time code.
              </p>
              <form className="rl__form" onSubmit={handleRequestOtp}>
                <div className="rl__field">
                  <label className="rl__label" htmlFor="forgot-phone">Mobile Number</label>
                  <div className="rl__phone-group">
                    <select
                      id="forgot-country-code"
                      className="rl__select"
                      value={countryCode}
                      onChange={e => setCountryCode(e.target.value)}
                      aria-label="Country code"
                    >
                      <option value="91">+91</option>
                      <option value="1">+1</option>
                      <option value="44">+44</option>
                      <option value="61">+61</option>
                      <option value="971">+971</option>
                    </select>
                    <input
                      type="tel"
                      id="forgot-phone"
                      className="rl__input"
                      value={phoneNumber}
                      onChange={e => setPhoneNumber(e.target.value.replace(/\D/g, ''))}
                      placeholder="Enter mobile number"
                      autoComplete="tel-national"
                      required
                      maxLength={15}
                    />
                  </div>
                </div>
                <button type="submit" className="rl__submit" disabled={loading}>
                  {loading ? <><Loader2 size={16} className="rl__spin" /> Sending OTP...</> : "Send OTP"}
                </button>
              </form>
            </>
          )}

          {mode === 'sms' && smsStep === 'otp' && (
            <form className="rl__form" onSubmit={handleSmsReset}>
              <p className="rl__otp-note">
                If an account is linked to +{countryCode} {phoneNumber}, an OTP has been
                sent to it. Enter the code and choose a new password.
              </p>
              {devOtp && (
                <p className="rl__otp-dev">
                  Development OTP <strong>{devOtp}</strong>
                </p>
              )}
              <div className="rl__field">
                <label className="rl__label" htmlFor="forgot-otp">Enter OTP</label>
                <input
                  type="text"
                  id="forgot-otp"
                  className="rl__input rl__otp-input"
                  value={otpCode}
                  onChange={e => setOtpCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  placeholder="······"
                  required
                  maxLength={6}
                  autoComplete="one-time-code"
                  inputMode="numeric"
                />
              </div>
              <div className="rl__field">
                <label className="rl__label" htmlFor="forgot-new-password">New password</label>
                <input
                  type="password"
                  id="forgot-new-password"
                  className="rl__input"
                  value={newPassword}
                  onChange={e => setNewPassword(e.target.value)}
                  placeholder="At least 8 characters"
                  autoComplete="new-password"
                  required
                  minLength={8}
                />
              </div>
              <div className="rl__field">
                <label className="rl__label" htmlFor="forgot-confirm-password">Confirm password</label>
                <input
                  type="password"
                  id="forgot-confirm-password"
                  className="rl__input"
                  value={confirmPassword}
                  onChange={e => setConfirmPassword(e.target.value)}
                  placeholder="Repeat the new password"
                  autoComplete="new-password"
                  required
                  minLength={8}
                />
              </div>
              <button type="submit" className="rl__submit" disabled={loading}>
                {loading ? <><Loader2 size={16} className="rl__spin" /> Resetting...</> : "Reset Password"}
              </button>
              <div className="rl__otp-actions">
                <button
                  type="button"
                  className="rl__link-btn"
                  onClick={handleResendOtp}
                  disabled={resendTimer > 0 || loading}
                >
                  {resendTimer > 0 ? `Resend in ${resendTimer}s` : 'Resend OTP'}
                </button>
                <button
                  type="button"
                  className="rl__link-btn"
                  onClick={() => {
                    setSmsStep('phone');
                    setOtpCode('');
                    setDevOtp('');
                    setError('');
                  }}
                >
                  Change number
                </button>
              </div>
            </form>
          )}
        </>
      )}
    </RoleLoginShell>
  );
}
